import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { Overview } from 'components/Overview'
import { AccountType, userHasAccountType } from 'config'
import { removeNonPagePermissions } from 'hooks/hasPagePermissions'
import { usePermissions } from 'hooks/usePermissions'
import NationalFlood from 'pages/Admin/Vendors/NationalFlood/NationalFlood'
import Notes from 'pages/Loan/Servicing/Notes/Notes'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import Api from 'services/api'
import { setLoanNumber } from 'utils/setLoanNumber'

import FraudGaurd from '../Underwriting/Dataverify/FraudGaurd'
import Lexis from '../Underwriting/Dataverify/Lexis'
import SSA from '../Underwriting/Dataverify/SSA'
import Tax from '../Underwriting/Dataverify/Tax'
import OrderOuts from '../Underwriting/OrderOuts'
import LoanDetailValidation from './LoanDetailValidation'
import LoanIntakeDocument from './LoanIntakeDocument'
import TridImportantDates from './TridImportantDates'

const externalLinks = ['orderValuation']

export default function LoanInTake() {
  const [selectedMenu, setSelectedMenu] = useState('')
  const [menuItems, setMenuItems] = useState<any>({})

  const history = useHistory()
  const location = useLocation()

  // Keep the sub-screen in the URL so it can be deep-linked (loan field lookup, bookmarks).
  const setMenu = (menuItem: string) => {
    setSelectedMenu(menuItem)
    history.replace(`/loanInTake/${setLoanNumber()}?menu=${menuItem}`)
  }

  const { auth } = useSelector((state: any) => {
    return {
      auth: state.auth,
    }
  })

  const { hasPermission } = usePermissions()

  useEffect(() => {
    if (Object.keys(menuItems).length) return
    let temp: any = {}
    if (hasPermission('Loan.InTake')) {
      if (!userHasAccountType(auth.profile, AccountType.ACCOUNT_EXECUTIVE)) {
        temp['nationalFlood'] = 'Flood'
        temp['dataverifySSA'] = 'DataVerify SSA'
        temp['dataverify4506'] = 'DataVerify 4506'
        temp['dataverifyDrive'] = 'DataVerify Drive'
        temp['lexis'] = 'SmartLinx Person Report'
      }
      temp['notes'] = 'Notes'
      temp['orderValuation'] = 'Order Valuation'
      temp['orderOuts'] = 'Order Outs'
      temp['loan_intake_documents'] = 'Loan Intake Documents'
      temp['loan_detail_validation'] = 'Loan Detail Validation'
      temp['trid_important_dates'] = 'TRID Important Dates'
    }
    removeNonPagePermissions(temp, ['pipeline', 'loanInTake'])
    setMenuItems(temp)
    const requested = decodeURI(new URLSearchParams(location.search).get('menu') || '')
    setSelectedMenu(temp[requested] ? requested : Object.keys(temp)[0])
  }, [auth])

  const renderFragment = useMemo(() => {
    switch (selectedMenu) {
      case 'nationalFlood':
        return <NationalFlood child={true} />
      case 'dataverifySSA':
        return <SSA />
      case 'dataverify4506':
        return <Tax />
      case 'dataverifyDrive':
        return <FraudGaurd />
      case 'lexis':
        return <Lexis />
      case 'notes':
        return <Notes child={true} />
      case 'orderOuts':
        return <OrderOuts />
      case 'loan_intake_documents':
        return <LoanIntakeDocument />
      case 'loan_detail_validation':
        return <LoanDetailValidation />
      case 'trid_important_dates':
        return <TridImportantDates />
      default: {
        return <div></div>
      }
    }
  }, [selectedMenu])

  const loanNumber = Api.getLoanNumber()

  const renderMenu = (menus: Record<string, string>) => {
    return (
      <ul className="sidebar-items flex flex-col p-4 pb-10">
        {Object.keys(menus).map((item: string, index) => {
          const isActive = selectedMenu === item
          if (externalLinks.indexOf(item) !== -1) {
            return (
              <li key={index} className="py-2 hover:underline cursor-pointer">
                <Link to={`/order_valuation/${loanNumber}`} target="_blank">
                  <span className="flex gap-2 items-center">
                    <span>
                      {index + 1}. {menus[item]}
                    </span>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4"></ArrowTopRightOnSquareIcon>
                  </span>
                </Link>
              </li>
            )
          }
          return (
            <li
              key={index}
              onClick={() => {
                setMenu(item)
              }}
              className="py-2"
            >
              <p className={`hover:underline cursor-pointer ${isActive ? 'border px-4 py-1 bg-zinc-100' : 'py-1'}`}>
                {index + 1}. {menus[item]}
              </p>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <div className="px-2 py-3 sm:px-4 sm:py-6">
      <Overview title="Loan Intake" />
      <div className="m-auto grid max-w-screen-2xl grid-cols-5 md:gap-6">
        <div className="sidebar-left col-span-5 mb-4 min-w-0 shrink-0 rounded bg-white shadow1 md:col-span-1">
          {renderMenu(menuItems)}
        </div>
        <div className="relative col-span-5 mb-4 h-fit min-w-0 rounded bg-white p-2 shadow1 content-right sm:p-4 md:col-span-4">
          <div className="min-w-0 overflow-x-auto">{renderFragment}</div>
        </div>
      </div>
    </div>
  )
}
